import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { LoaderCircle } from 'lucide-react';
import { useRipple } from './use-ripple';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'bare';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  loading?: boolean;
  fullWidth?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    startIcon,
    endIcon,
    children,
    className = '',
    type = 'button',
    disabled,
    onPointerDown,
    onClick,
    'aria-busy': ariaBusy,
    ...props
  },
  ref,
) {
  const busy = loading || ariaBusy === true || ariaBusy === 'true';
  const inactive = disabled || loading;
  const ripple = useRipple(Boolean(inactive));
  return (
    <button
      {...props}
      ref={ref}
      type={type}
      disabled={inactive}
      aria-busy={busy || undefined}
      className={`ui-button ui-button--${variant} ui-button--${size}${fullWidth ? ' ui-button--full' : ''} ${className}`.trim()}
      onPointerDown={(event) => {
        onPointerDown?.(event);
        if (!event.defaultPrevented) ripple.pointerDown(event);
      }}
      onClick={(event) => {
        if (!inactive) {
          // Safari does not focus buttons on pointer activation; dialog return focus needs an opener.
          event.currentTarget.focus({ preventScroll: true });
          ripple.click(event);
          onClick?.(event);
        }
      }}
    >
      {busy ? <LoaderCircle className="ui-spinner" size={17} aria-hidden="true" /> : startIcon}
      {children}
      {endIcon}
      {ripple.ripples}
    </button>
  );
});
